import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, Cake as CakeIcon } from '@mui/icons-material';

const API_URL = "https://wg.pizzahub.lk/api";

export default function Customers() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [customers,   setCustomers]   = useState([]);
  const [openModal,   setOpenModal]   = useState(false);
  const [isEditMode,  setIsEditMode]  = useState(false);
  const [customerData,setCustomerData]= useState({
    id: null,
    name: '',
    email: '',
    phone: '',
    dob:   '',      // YYYY‑MM‑DD
  });

  // fetch list
  const fetchCustomers = () => {
    fetch(`${API_URL}/get_customers.php`)
      .then(r=>r.json())
      .then(setCustomers)
      .catch(console.error);
  };
  useEffect(fetchCustomers, []);

  // open modal
  const handleOpenModal = (cust = null) => {
    if (cust) {
      setCustomerData({
        id:    cust.id,
        name:  cust.name,
        email: cust.email || '',
        phone: cust.phone,
        dob:   cust.dob || '',
      });
      setIsEditMode(true);
    } else {
      setCustomerData({ id:null, name:'', email:'', phone:'', dob:'' });
      setIsEditMode(false);
    }
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setCustomerData({ id:null, name:'', email:'', phone:'', dob:'' });
  };

  // input change
  const handleInputChange = e => {
    const { name, value } = e.target;
    setCustomerData(cd => ({ ...cd, [name]: value }));
  };

  // save (add/update)
  const handleSave = () => {
    const url = isEditMode
      ? `${API_URL}/update_customer.php`
      : `${API_URL}/add_customer.php`;

    fetch(url, {
      method: 'POST',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify(customerData),
    })
    .then(r=>r.json())
    .then(d=>{
      if (d.status==='success') {
        fetchCustomers();
        handleCloseModal();
      } else {
        console.error('Error saving customer:', d.message);
      }
    })
    .catch(console.error);
  };

  // delete
  const handleDelete = id => {
    if (!window.confirm('Delete this customer?')) return;
    fetch(`${API_URL}/delete_customer.php`, {
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify({ id }),
    })
    .then(r=>r.json())
    .then(d=>{
      if (d.status==='success') fetchCustomers();
      else console.error(d.message);
    })
    .catch(console.error);
  };

  // send birthday wishes
  const sendBirthdayWishes = () => {
    if (!window.confirm("Send birthday wishes to everyone whose DOB is today?")) return;
    fetch(`${API_URL}/birthday_wish.php`)
      .then(r=>r.json())
      .then(d=>{
        if (d.status==='success') {
          alert(`Sent wishes to ${d.sent_count} customer(s).`);
        } else {
          alert("Error: "+d.message);
        }
      })
      .catch(err=>{
        console.error(err);
        alert("Network error");
      });
  };

  return (
    <Box sx={{ p:2 }}>
      <Box sx={{ display:'flex', alignItems:'center', mb:2, gap:2 }}>
        <Typography variant="h4" flexGrow={1}>Customers</Typography>
        <Button
          variant="outlined"
          startIcon={<CakeIcon/>}
          onClick={sendBirthdayWishes}
        >
          Birthday Wishes
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon/>}
          onClick={()=>handleOpenModal(null)}
        >
          Add Customer
        </Button>
      </Box>

      {isMobile ? (
        <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          {customers.length ? customers.map(c=>(
            <Card key={c.id} variant="outlined">
              <CardContent>
                <Typography variant="h6">{c.name}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {c.email||'—'}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {c.phone}</Typography>
                <Typography variant="body2"><strong>DOB:</strong> {c.dob||'—'}</Typography>
              </CardContent>
              <CardActions>
                <IconButton onClick={()=>handleOpenModal(c)} color="primary"><EditIcon/></IconButton>
                <IconButton onClick={()=>handleDelete(c.id)} color="error"><DeleteIcon/></IconButton>
              </CardActions>
            </Card>
          )) : (
            <Typography align="center">No customers found.</Typography>
          )}
        </Box>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>DOB</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map(c=>(
                <TableRow key={c.id}>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.email||'—'}</TableCell>
                  <TableCell>{c.phone}</TableCell>
                  <TableCell>{c.dob||'—'}</TableCell>
                  <TableCell align="center">
                    <IconButton onClick={()=>handleOpenModal(c)} color="primary"><EditIcon/></IconButton>
                    <IconButton onClick={()=>handleDelete(c.id)} color="error"><DeleteIcon/></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!customers.length && (
                <TableRow>
                  <TableCell colSpan={5} align="center">No customers found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* add/edit */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth>
        <DialogTitle>{isEditMode?"Edit Customer":"Add New Customer"}</DialogTitle>
        <DialogContent sx={{ display:'flex', flexDirection:'column', gap:2, pt:2 }}>
          <TextField
            label="Name" name="name" variant="standard"
            fullWidth autoFocus
            value={customerData.name}
            onChange={handleInputChange}
          />
          <TextField
            label="Email (optional)" name="email" variant="standard"
            fullWidth
            value={customerData.email}
            onChange={handleInputChange}
          />
          <TextField
            label="Phone" name="phone" variant="standard"
            fullWidth
            value={customerData.phone}
            onChange={handleInputChange}
          />
          <TextField
            label="Date of Birth"
            name="dob"
            type="date"
            InputLabelProps={{ shrink:true }}
            variant="standard"
            fullWidth
            value={customerData.dob}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
